import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { CategoryService } from '../../pages/category-page/category.service';
import { SidebarMenuComponent } from './sidebar-menu/sidebar-menu.component';
import { ToolbarService } from '../../services/toolbar.service';
import { ServiceMenuComponent } from '../service-menu/service-menu.component';
import { RouterLinkWithLang } from '../../directives/router-link-with-lang/router-link-with-lang.directive';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { CatalogMenuComponent } from './catalog-menu/catalog-menu.component';
import { SearchBarComponent } from './search-bar/search-bar.component';
import { LangSelectorComponent } from '../lang-selector/lang-selector.component';
import { MiniAccountComponent } from './mini-account/mini-account.component';
import { TranslatePipe } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { MANAGER_PHONES, PHONE_NUMBER_MASK } from '../../constants';
import { NgxMaskPipe } from 'ngx-mask';
import { IconComponent } from "../icon/icon.component";
import { WishlistService } from '../../services/wishlist.service';
import { CartModalComponent, CartModalData } from '../cart-modal/cart-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { CartService } from '../../services/cart.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    ServiceMenuComponent,
    ClickOutsideDirective,
    CatalogMenuComponent,
    SearchBarComponent,
    LangSelectorComponent,
    MiniAccountComponent,
    SidebarMenuComponent,
    TranslatePipe,
    RouterLink,
    RouterLinkWithLang,
    NgxMaskPipe,
    IconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent {
  protected readonly categoryService = inject(CategoryService);
  protected readonly toolbarService = inject(ToolbarService);
  protected readonly wishlistService = inject(WishlistService);
  readonly cartService = inject(CartService);
  readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  isSearchBarInFocus = signal(false);
  isHeaderCatalogMenuVisible = signal(false);
  isCatalogMenuOpenedWithCatalogBtn = signal(false);

  readonly isSidebarCatalog = input<boolean>(false);
  readonly sidebarCmp = viewChild(SidebarMenuComponent);
  private readonly toolbarRef = viewChild.required<ElementRef<HTMLElement>>('toolbarRef');

  constructor() {
    afterNextRender(() => this.watchToolbarHeight());
  }

  openMenu() {
    this.sidebarCmp().openMenu();
  }

  public setIsSearchBarInFocus(isSearchBarInFocus: boolean): void {
    this.isSearchBarInFocus.set(isSearchBarInFocus);
  }

  public toggleHeaderCatalogMenu(isClosed = false): void {
    if (isClosed) {
      this.isHeaderCatalogMenuVisible.set(false);
      this.isCatalogMenuOpenedWithCatalogBtn.set(false);
    } else {
      this.isCatalogMenuOpenedWithCatalogBtn.update(v => !v);
      this.isHeaderCatalogMenuVisible.update(v => !v);
    }
  }

  public onMenuMouseLeave(): void {
    if (this.isHeaderCatalogMenuVisible() && !this.isCatalogMenuOpenedWithCatalogBtn()) {
      this.isHeaderCatalogMenuVisible.set(false);
    }
  }

  openCatalogFromSidebarHover(): void {
    this.isHeaderCatalogMenuVisible.set(true);
  }

  protected openCart(): void {
    this.dialog.open<CartModalComponent, CartModalData>(
      CartModalComponent,
      { data: { sourceForAnalytics: 'header' } },
    );
  }

  /**
   * Below `$media_medium` the toolbar is fixed to the top of the screen, and everything else
   * pinned there has to sit under it rather than behind it. From there up it scrolls with the
   * page and takes no room.
   */
  private watchToolbarHeight(): void {
    const el = this.toolbarRef().nativeElement;
    const observer = new ResizeObserver(() => {
      // A cached page's header is taken out of the document; the one on screen reports.
      if (!el.isConnected) { return; }

      const isFixed = getComputedStyle(el).position === 'fixed';
      this.toolbarService.stickyHeaderElHeight.set(isFixed ? el.getBoundingClientRect().height : 0);
    });
    observer.observe(el);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  protected readonly MANAGER_PHONES = MANAGER_PHONES;
  protected readonly PHONE_NUMBER_MASK = PHONE_NUMBER_MASK;
}
